/**
 * Råsignaler för varför mikrofonen inte startade (IM-128). Alla värden är
 * fasta band/namn - aldrig webbläsarens fria feltext - så att orsaken kan
 * avgöras i analysen i stället för att gissas här: samma `NotAllowedError`
 * betyder "sa nej", "stängde frågan", "spärrad" eller "nekad i telefonens
 * inställningar" beroende på webbläsare, väntetid och behörighetsläge.
 */

export type MicPermission = 'granted' | 'prompt' | 'denied' | 'unknown'

const KNOWN_ERRORS = [
	'NotAllowedError',
	'NotFoundError',
	'NotReadableError',
	'OverconstrainedError',
	'AbortError',
	'SecurityError',
	'TypeError',
] as const

export type MicErrorName = (typeof KNOWN_ERRORS)[number] | 'other'

export function micErrorName(err: unknown): MicErrorName {
	const name = (err as { name?: unknown } | null)?.name
	return (KNOWN_ERRORS as readonly unknown[]).includes(name)
		? (name as MicErrorName)
		: 'other'
}

/**
 * Chromiums feltexter skiljer fallen åt (user_media_processor.cc); Safari och
 * Firefox har en text för allt och blir `other`. Tolka därför bara värdet
 * tillsammans med `visit:browser`.
 */
export function micErrorDetail(
	err: unknown,
): 'denied' | 'dismissed' | 'system' | 'not_found' | 'other' {
	const message = String((err as { message?: unknown } | null)?.message ?? '')
	if (/^permission dismissed/i.test(message)) return 'dismissed'
	if (/^permission denied by system/i.test(message)) return 'system'
	if (/^permission denied/i.test(message)) return 'denied'
	if (/^requested device not found/i.test(message)) return 'not_found'
	return 'other'
}

/** Tid i getUserMedia. Under ~50 ms hann ingen fråga visas; en människa
 * svarar på hundratals ms. Gränsen dras i analysen, banden är finare. */
export function micWaitBucket(ms: number) {
	if (ms < 50) return '<50ms'
	if (ms < 250) return '50-250ms'
	if (ms < 1000) return '250ms-1s'
	if (ms < 5000) return '1-5s'
	return '>5s'
}

// Behörigheten läses i förväg och hålls aktuell via onchange, så att
// getUserMedia aldrig väntar på en extra asynkron runda efter trycket.
let permission: MicPermission = 'unknown'
let primed = false

async function queryMicPermission(): Promise<PermissionStatus | null> {
	try {
		// Firefox kastar för 'microphone'; äldre Safari saknar API:t.
		return (
			(await navigator.permissions?.query({
				name: 'microphone' as PermissionName,
			})) ?? null
		)
	} catch {
		return null
	}
}

export function primeMicPermission() {
	if (primed || typeof navigator === 'undefined') return
	primed = true
	void queryMicPermission().then(status => {
		if (!status) return
		permission = status.state
		status.onchange = () => {
			permission = status.state
		}
	})
}

export function currentMicPermission(): MicPermission {
	return permission
}

/** Läget efter ett fel - högst `timeoutMs`, så att spårningen aldrig hänger. */
export async function micPermissionAfter(
	timeoutMs = 300,
): Promise<MicPermission> {
	const status = await Promise.race([
		queryMicPermission(),
		new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs)),
	])
	return status?.state ?? 'unknown'
}

// Per sidladdning (modulglobalt, överlever SPA-navigering och modalen som
// öppnas igen). Omtryck på en spärr blir då attempt 2, 3+ i stället för nya
// "första" försök, och hjälptextens effekt syns i försöken efter den.
let attempts = 0
let helpShown = false

export function nextMicAttempt(): '1' | '2' | '3+' {
	attempts += 1
	return attempts === 1 ? '1' : attempts === 2 ? '2' : '3+'
}

export function markMicHelpShown() {
	helpShown = true
}

export function micHelpShownBefore() {
	return helpShown
}

/** Bara för tester. */
export function resetMicDiagnostics() {
	permission = 'unknown'
	primed = false
	attempts = 0
	helpShown = false
}
